import React from 'react';
import {
  ChartBarIcon,
  CurrencyPoundIcon,
  CalendarDaysIcon,
  ArrowUpOnSquareIcon,
  SparklesIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  CogIcon,
  TableCellsIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  BanknotesIcon,
  ScaleIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  PlusCircleIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CubeIcon,
} from '@heroicons/react/24/outline';

export default function StaffCostsHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-600 rounded-full mb-6">
            <CurrencyPoundIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Staff Costs & Financial Management</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Master your workforce spending with intelligent budget tracking, cost analysis, hiring insights, and full project & subscription revenue oversight
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Financial Dashboard?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your complete financial command centre. It combines real‑time staff expenditure, budget tracking,
                integrated revenue (from projects and subscriptions), expenses, profit, and payroll data – all in one place.
                You can monitor daily costs, set department budgets, analyse profitability, track project performance,
                manage subscription revenue, set revenue milestones, and export payroll or project data for Sage and Xero.
                It's designed to give you full visibility of your people‑related and project‑related finances, from shift
                costs to bottom‑line profit.
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Navigation Flow */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Dashboard Navigation Flow</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Monitor & Budget</h3>
              <p className="text-gray-600 text-sm">View daily, monthly, and yearly staff expenditure across departments and set intelligent budgets</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Project & Subscription Revenue</h3>
              <p className="text-gray-600 text-sm">Track revenue from external projects and customer subscriptions, with LTV and period equivalents</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Integrated Profit & Expenses</h3>
              <p className="text-gray-600 text-sm">See profit after staff costs and expenses; add and track recurring or one‑time business expenses</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payroll & CSV Exports</h3>
              <p className="text-gray-600 text-sm">Use pay windows to filter shifts, review SSP, and export Sage/Xero CSV files for payroll and projects</p>
            </div>
          </div>
        </div>

        {/* Key Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <CalendarDaysIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real‑time Expenditure</h3>
            <p className="text-gray-600 text-sm">Live tracking of daily staff costs based on actual shift data, with monthly and yearly projections</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <CurrencyPoundIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Budget Management</h3>
            <p className="text-gray-600 text-sm">Set monthly budgets and allocate funds across departments with visual allocation tracking and over‑budget warnings</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <BriefcaseIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Project & Subscription Revenue</h3>
            <p className="text-gray-600 text-sm">Track revenue from external projects and customer subscriptions; view LTV and period equivalents</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <BanknotesIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Expense Tracking</h3>
            <p className="text-gray-600 text-sm">Add recurring (daily/weekly/monthly/annual) or one‑time expenses; they are automatically deducted from profit</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <DocumentArrowDownIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Payroll & Project Exports</h3>
            <p className="text-gray-600 text-sm">Export shift, holiday, sick pay, and project data as Sage‑compatible or Xero‑compatible CSV files</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Hiring & Milestone Intelligence</h3>
            <p className="text-gray-600 text-sm">Data‑driven hiring recommendations and revenue milestones to track performance against targets</p>
          </div>
        </div>

        {/* Project Financials Dashboard */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Project Financials Dashboard</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <BriefcaseIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Revenue Sources</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                External projects contribute one‑off revenue, while customer subscriptions provide recurring income. The dashboard shows weekly, monthly, quarterly, and yearly equivalents, as well as Lifetime Value (LTV) for each active contract.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm"><strong>Pro Tip:</strong> Use the time filter tabs to view projects started in a specific period (weekly/monthly/quarterly/yearly).</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Revenue Milestones</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Set targets for weekly, monthly, quarterly, or yearly revenue. The dashboard shows actual revenue against the target on the revenue trend chart, helping you track performance and adjust strategy.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">Admin users can create new milestones directly from the dashboard.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DocumentArrowDownIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Project CSV Exports</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Download Normal, Sage, or Xero‑compatible CSV files for projects filtered by any time period. Includes project ID, name, client, industry, dates, price, costs, profit, margin, and more.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <p className="text-purple-800 text-sm">Exports are ready for import into accounting software – save hours of manual data entry.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ScaleIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Profit Margin Visualisation</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Each project card displays a circular gauge showing its profit margin (green ≥30%, yellow 20-29%, red below 20%). This gives an instant visual cue of project profitability.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <p className="text-amber-800 text-sm">Hover over the charts to see detailed tooltips with project names and exact amounts.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Integrated Finance Dashboard */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Integrated Finance Dashboard</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Revenue & Profit</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Total revenue comes from external projects and customer subscriptions. Staff costs are calculated from contracted or average hours multiplied by hourly rates. Profit is revenue minus staff costs minus expenses.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm"><strong>Pro Tip:</strong> Use the period comparison chart to see weekly, monthly, quarterly, and yearly performance at a glance.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <PlusCircleIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Expense Management</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Add recurring or one‑time expenses. Recurring expenses are multiplied by frequency (daily/weekly/monthly/annual) and applied to each period. One‑time expenses are counted in the period of their start date.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">The cost breakdown pie chart shows how much of your total costs are staff vs. expenses for the latest month.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ScaleIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">6‑Month Trend</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                The 6‑month trend chart shows revenue, profit, and total costs (staff + expenses) over the last six months, helping you identify seasonal patterns and performance trends.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <p className="text-purple-800 text-sm">The period comparison chart (bar chart) compares revenue, staff costs, expenses, and profit across weekly, monthly, quarterly, and yearly periods.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TableCellsIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Monthly Breakdown</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                The monthly breakdown table shows revenue, staff cost, expenses, and profit for each of the last six months, making it easy to compare month‑over‑month performance.
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-red-800 text-sm">Red profit values indicate a loss – use the breakdown to quickly pinpoint which months are underperforming.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payroll & ClockIn Table */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Payroll & ClockIn Table</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CalendarDaysIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Pay Windows</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Use the pay date policies to define payroll periods. Select a month and a pay window, then apply an offset to adjust the data range. The selected window shows total pay and hours for the filtered period.
              </p>
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                <p className="text-indigo-800 text-sm">The offset allows you to exclude shifts that fall outside the true payroll period, keeping your export accurate.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Statutory Sick Pay (SSP)</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                From 6 April 2026, SSP is paid from day one of sickness. The table calculates eligibility based on average weekly earnings and shows daily SSP amounts. You can approve SSP for sick leave records.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <p className="text-amber-800 text-sm">Zero‑hours contracts are checked against an £125 weekly earnings threshold; standard contracts use their contracted weekly earnings.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DocumentArrowDownIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Sage & Xero Exports</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Download CSV files formatted for Sage (employee reference, date, pay code, units, rate, amount, narrative) or Xero (date, employee name, reference, NI number, pay item, units, rate, amount, status, description).
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">The export includes all filtered clock‑ins, holiday days, and sick pay (SSP) – making payroll processing quick and error‑free.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Shift End Source</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                For each shift, you can choose whether to use the scheduled <em>End Time</em> or the actual <em>ClockOut</em> for pay calculations. This gives you flexibility to handle overtime or early finishes.
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-red-800 text-sm">Unpaid break deductions are applied automatically for shifts longer than 6 hours.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contract Types */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Understanding Contract Types</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">FT</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fixed Hours Contracts</h3>
              <p className="text-sm text-gray-600">Uses ContractedHours for calculations. Standard full‑time or part‑time positions with guaranteed hours.</p>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">0H</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Zero Hours Contracts</h3>
              <p className="text-sm text-gray-600">Uses AverageHours for calculations. Flexible contracts where hours vary based on business needs.</p>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">CALC</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Cost Calculations</h3>
              <p className="text-sm text-gray-600">Monthly costs = (Hours × 4 weeks) × Hourly Rate. Accurate projections for budget planning.</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 md:p-8 border border-orange-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Best Practices for Financial Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Set realistic budgets based on historical spending patterns and business growth projections</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Monitor daily expenditure to catch budget overruns early and adjust staffing accordingly</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the integrated finance dashboard to see the full picture of revenue, costs, and profit</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Add all business expenses (rent, software, etc.) to get an accurate profit figure</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Export payroll CSV files regularly to keep Sage/Xero up to date with minimal manual effort</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the 6‑month trend to spot seasonal variations and plan for busy/slow periods</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Balance budget allocations to prevent over‑reliance on any single department</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Create vacancies based on budget capacity rather than just business needs</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Review the cost breakdown pie chart to see if expenses are growing faster than staff costs</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Approve SSP only after verifying eligibility – the table gives you all the data you need</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Track project profit margins individually to identify which projects are most (or least) profitable</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use the subscription revenue table to monitor recurring income and identify renewal opportunities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}